import { useCallback, useEffect } from 'react';
import useFetchWithMsal from '../useFetchWithMsal';
import { protectedResources } from '../../authConfig';
import { useChemoFetch } from './useChemoFetch';
import { useChemoRecordsEdit } from './useChemoRecordsEdit';
import { useChemoUI } from './useChemoUI';
import { useChemoSearch } from './useChemoSearch';
import { chemoTranslations } from './chemoTranslations';
import { SEND_CUSTOMER_MESSAGE_ENDPOINT, HTTP_METHOD_POST } from './chemoConstants';

export const useChemoRecords = () => {
    const { execute } = useFetchWithMsal({
        scopes: protectedResources.apiScopes.scopes.read,
    });

    const fetch = useChemoFetch(execute);
    const ui = useChemoUI();
    const search = useChemoSearch();

    const edit = useChemoRecordsEdit({
        fetchedRecords: fetch.fetchedRecords,
        setFetchedRecords: fetch.setFetchedRecords,
        originalRecords: fetch.originalRecords,
        KLINE: fetch.KLINE,
        execute,
        fetchReports: fetch.fetchReports,
        setLoading: fetch.setLoading,
        setError: fetch.setError
    });

    useEffect(() => {
        edit.syncEditedRecords(fetch.fetchedRecords);
    }, [fetch.fetchedRecords, edit.syncEditedRecords]);

    const submitReport = useCallback(async (reportData) => {
        fetch.setLoading(true);
        fetch.setError(null);
        try {
            const result = await fetch.fetchChemoRecords(reportData);
            if (result.success) {
                await fetch.fetchReports();
            }
            return result;
        } catch (err) {
            fetch.setError(err.message || chemoTranslations.errors.submitError);
            return { success: false, error: err.message };
        } finally {
            fetch.setLoading(false);
        }
    }, [fetch]);

    const handleSubmit = useCallback(async (reportData) => {
        return await fetch.fetchChemoRecords(reportData);
    }, [fetch.fetchChemoRecords]);

    const handleSaveReport = useCallback(async (reportData) => {
        const result = await edit.saveChemoReport(reportData);

        if (result.data && (result.data.results || result.data.errors)) {
            ui.showResultDialog(result.data);
        } else {
            if (result.success) {
                ui.showSnackbar(chemoTranslations.success.reportSaved, 'success');
            }
        }

        if (result.success) {
            ui.closeForm();
        }

        return result;
    }, [edit.saveChemoReport, ui.showResultDialog, ui.showSnackbar, ui.closeForm]);

    const handleTopRecordsChange = useCallback((newTop, hospital) => {
        fetch.handleTopChange(newTop);
        if (hospital) {
            fetch.fetchHospitalReport(hospital, newTop);
        }
    }, [fetch.handleTopChange, fetch.fetchHospitalReport]);

    const handleDateSearch = useCallback(async () => {
        if (!search.dateSearch.startDate || !search.dateSearch.endDate) {
            ui.showSnackbar(chemoTranslations.errors.dateRangeRequired, 'warning');
            return;
        }

        const result = await fetch.fetchReportsByDate(
            search.dateSearch.startDate,
            search.dateSearch.endDate
        );
        if (!result.success) {
            ui.showSnackbar(`${chemoTranslations.errors.errorPrefix}${result.error}`, 'error');
        }
    }, [search.dateSearch, fetch.fetchReportsByDate, ui.showSnackbar]);

    const openNewReportForm = useCallback(() => {
        ui.openNewReportForm();
        fetch.resetRecords();
        edit.resetEditedRecords();
    }, [ui.openNewReportForm, fetch.resetRecords, edit.resetEditedRecords]);

    const handleSendMessage = useCallback(async (hospital) => {
        const message = ui.messageDialog.message?.trim();
        if (!message) return;

        ui.setMessageSending(true);
        try {
            await execute(
                HTTP_METHOD_POST,
                `${process.env.REACT_APP_BACKEND_BASE_URL}${SEND_CUSTOMER_MESSAGE_ENDPOINT}`,
                { message, hospital }
            );
            ui.closeMessageDialog();
            ui.showSnackbar(chemoTranslations.success.messageSent, 'success');
        } catch (err) {
            ui.showSnackbar(chemoTranslations.errors.messageSendError, 'error');
        } finally {
            ui.setMessageSending(false);
        }
    }, [execute, ui]);

    return {
        reports: fetch.reports,
        loading: fetch.loading,
        error: fetch.error,
        searchTerm: search.searchTerm,
        showForm: ui.showForm,
        currentReport: ui.currentReport,
        fetchedRecords: fetch.fetchedRecords,
        editedRecords: edit.editedRecords,
        isLoadingRecords: fetch.isLoadingRecords,
        topRecords: fetch.topRecords,
        fetchReports: fetch.fetchReports,
        submitReport,
        fetchChemoRecords: fetch.fetchChemoRecords,
        fetchHospitalReport: fetch.fetchHospitalReport,
        fetchReportsByDate: fetch.fetchReportsByDate,
        updateEditedRecords: edit.updateEditedRecords,
        handleCheckboxChange: edit.handleCheckboxChange,
        handleFieldChange: edit.handleFieldChange,
        formatDate: search.formatDate,
        saveChemoReport: edit.saveChemoReport,
        openNewReportForm,
        openExistingReport: ui.openExistingReport,
        closeForm: ui.closeForm,
        filterReports: search.filterReports,
        handleSearchChange: search.handleSearchChange,
        handleTopChange: fetch.handleTopChange,
        filteredReports: search.filterReports(fetch.reports, search.searchTerm),

        snackbar: ui.snackbar,
        showSnackbar: ui.showSnackbar,
        closeSnackbar: ui.closeSnackbar,
        resultDialog: ui.resultDialog,
        showResultDialog: ui.showResultDialog,
        closeResultDialog: ui.closeResultDialog,
        dateSearch: search.dateSearch,
        updateDateSearch: search.updateDateSearch,

        handleSubmit,
        handleSaveReport,
        handleTopRecordsChange,
        handleDateSearch,

        messageDialog: ui.messageDialog,
        openMessageDialog: ui.openMessageDialog,
        closeMessageDialog: ui.closeMessageDialog,
        updateMessageText: ui.updateMessageText,
        handleSendMessage
    };
};
